import React from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  LinkedIn, 
  YouTube 
} from '@mui/icons-material';
import './Footer.css';

const Footer: React.FC = () => {

  const footerSections = [
    {
      title: "Services",
      links: [
        { name: "Ride", href: "/services/ride" },
        { name: "Market", href: "/services/market" },
        { name: "Flash", href: "/services/flash" },
        { name: "Truck", href: "/services/truck" },
        { name: "Quickko for Business", href: "/services/business" },
      ],
    },
    {
      title: "Food",
      links: [
        { name: "Bakeries", href: "/services/food/bakeries" },
        { name: "Sweet Shops", href: "/services/food/sweet-shops" },
        { name: "Home Food Shops", href: "/services/food/home-food" },
        { name: "Pickle Shops", href: "/services/food/pickles" },
      ],
    },
    {
      title: "Fresh",
      links: [
        { name: "Dairy Shops", href: "/services/fresh/dairy" },
        { name: "Fruits & Vegetables", href: "/services/fresh/fruits-vegetables" },
        { name: "Flowers", href: "/services/fresh/flowers" },
        { name: "Meat Shops", href: "/services/fresh/meat" },
      ],
    },
    {
      title: "Mart",
      links: [
        { name: "Kirana", href: "/services/mart/kirana" },
        { name: "Organic Shops", href: "/services/mart/organic" },
        { name: "Rice Traders", href: "/services/mart/rice" },
        { name: "Dry Fruit Stores", href: "/services/mart/dry-fruits" },
        { name: "Electricals", href: "/services/mart/electricals" },
      ],
    },
    {
      title: "Health & Wellness",
      links: [
        { name: "Pharmacies", href: "/services/health/pharmacies" },
        { name: "Ayurvedic Shops", href: "/services/health/ayurvedic" },
        { name: "Dental Clinics", href: "/services/health/dental" },
      ],
    },
    {
      title: "Company",
      links: [
        { name: "About Us", href: "/company" },
        { name: "Careers", href: "/careers" },
        { name: "Blog", href: "/blog" },
        { name: "Press", href: "/press" },
      ],
    },
    {
      title: "Resources",
      links: [
        { name: "Help Center", href: "/help" },
        { name: "Safety", href: "/safety" },
        { name: "Support", href: "/support" },
        { name: "Terms of Service", href: "/terms" },
        { name: "Privacy Policy", href: "/privacy" },
      ],
    },
  ];


  const socialLinks = [
    { icon: Facebook, href: "#", label: "Facebook" },
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Instagram, href: "#", label: "Instagram" },
    { icon: LinkedIn, href: "#", label: "LinkedIn" },
    { icon: YouTube, href: "#", label: "YouTube" },
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      return;
    }
    // Let React Router handle navigation
  };

  return (
    <footer className="footer" role="contentinfo">
      <div className="footer-container">
        {/* Top Section */}
        <div className="footer-top">
          <div className="footer-brand-section">
            <div className="footer-brand-row">
              <Logo size="large" variant="light" showTagline={true} />
              {/* App Store Buttons */}
              <div className="footer-app-buttons">
                <div className="footer-app-button">
                  <div className="app-button-icon">
                    <span>📱</span>
                  </div>
                  <div className="app-button-text">
                    <p className="app-button-label">Download on</p>
                    <p className="app-button-name">App Store</p>
                  </div>
                </div>
                <div className="footer-app-button">
                  <div className="app-button-icon">
                    <span>🤖</span>
                  </div>
                  <div className="app-button-text">
                    <p className="app-button-label">Get it on</p>
                    <p className="app-button-name">Google Play</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="footer-contact">
            <p className="footer-contact-label">Questions? Reach out to us</p>
            <a href="mailto:hello@quickko.com" className="footer-contact-email">
              hello@quickko.com
            </a>
          </div>
        </div>

        {/* Links Grid */}
        <div className="footer-links-grid">
          {footerSections.map((section) => (
            <div key={section.title} className="footer-column">
              <h4 className="footer-column-title">{section.title}</h4>
              <ul className="footer-column-links">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="footer-link"
                      onClick={(e) => handleLinkClick(e, link.href)}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="footer-bottom">
          <p className="footer-copyright">
            © {new Date().getFullYear()} Quickko. All rights reserved.
          </p>

          {/* Social Links */}
          <div className="footer-social">
            {socialLinks.map((social) => {
              const IconComponent = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="footer-social-link"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  <IconComponent className="footer-social-icon" />
                </a>
              );
            })}
          </div>

          {/* Legal & Support Links */}
          <div className="footer-legal">
            <Link
              to="/terms"
              className="footer-legal-link"
              onClick={(e) => handleLinkClick(e, "/terms")}
            >
              Terms of Service
            </Link>
            <Link
              to="/privacy"
              className="footer-legal-link"
              onClick={(e) => handleLinkClick(e, "/privacy")}
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
